import React from 'react'
import type { PlanId } from '@/lib/branchTypes'
import { capabilityMilestones, getCapabilityMarkerSpecs } from '@/app/playbook/extrapolated-graph/capabilityThresholds'
import {
  DEFAULT_SCALE_SETTINGS,
  EC_TO_UPLIFT_REFERENCE_POINTS,
  CAPABILITIES_FUTURE_LINE_DASHARRAY,
  CAPABILITIES_FUTURE_LINE_STROKE_WIDTH,
  CAPABILITIES_LINE_COLOR,
  GRAPH_LABEL_FADE_DURATION_MS,
  GRAPH_LINE_STROKE_WIDTH,
  PROJECTED_LOSS_OF_CONTROL_2028_OPACITY,
  PROJECTED_LOSS_OF_CONTROL_2028_SATURATION,
  PROJECTED_LOSS_OF_CONTROL_COLOR,
  PROJECTED_LOSS_OF_CONTROL_LOW_COLOR,
} from './graphConstants'
import type {
  CapabilityThresholdOverlay,
  CurrentGraphPoint,
  ExtrapolatedGraphLineSegment,
  ExtrapolatedGraphPointMarker,
  ExtrapolatedGraphScaleOverrides,
  ExtrapolatedGraphScaleSettings,
  FormattedGraphValue,
  GalaxyScaleSettings,
  GraphDatum,
  GraphLineRenderBounds,
  GraphLineSource,
  GraphMarkerSource,
  GraphPadding,
  GraphPoint,
  GraphRenderSourceState,
  TimestampPosition,
  TimelineScaleSettings,
  VisibleGraphState,
  YScaleSettings,
} from './types'

// User Timing measures feed the dev perf tooling only — each one emits a
// trace event, a real per-frame cost whenever DevTools/tracing is attached,
// so they must not run in production.
export const devPerfMeasure: (
  name: string,
  options?: PerformanceMeasureOptions
) => void =
  process.env.NODE_ENV === 'development'
    ? (name, options) => { performance.measure(name, options) }
    : () => undefined

export const formatSpeedupValue = (value: number): FormattedGraphValue => {
  if (value >= 100) {
    const roundedValue = Math.round(value)
    return {
      text: `${roundedValue.toLocaleString()}x`,
      numberValue: roundedValue,
      suffix: 'x',
      maximumFractionDigits: 0,
    }
  }
  if (value >= 10) {
    const roundedValue = Number.parseFloat(value.toFixed(1))
    return {
      text: `${roundedValue}x`,
      numberValue: roundedValue,
      suffix: 'x',
      maximumFractionDigits: 1,
    }
  }
  const roundedValue = Number.parseFloat(value.toFixed(2))
  return {
    text: `${roundedValue}x`,
    numberValue: roundedValue,
    suffix: 'x',
    maximumFractionDigits: 2,
  }
}

export const formatSpeedupValueWithTwoSignificantDigits = (value: number) => {
  if (value >= 10) return formatSpeedupValue(value).text
  return `${Number.parseFloat(value.toPrecision(2))}x`
}

// Hover tooltips clamp the displayed speedup: past 1000x the exact figure is
// model noise, so show ">1,000x" instead of a spuriously precise number.
const SPEEDUP_HOVER_DISPLAY_MAX = 1000

export const formatSpeedupHoverText = (value: number) => {
  if (value > SPEEDUP_HOVER_DISPLAY_MAX) {
    return `>${SPEEDUP_HOVER_DISPLAY_MAX.toLocaleString()}x`
  }
  return formatSpeedupValueWithTwoSignificantDigits(value)
}

export const getInterpolatedUpliftFromEffectiveCompute = (effectiveCompute: number) => {
  const referenceCurvePoints = EC_TO_UPLIFT_REFERENCE_POINTS
  if (effectiveCompute <= referenceCurvePoints[0].effectiveCompute) {
    return referenceCurvePoints[0].uplift
  }
  for (let index = 1; index < referenceCurvePoints.length; index++) {
    const previousReferencePoint = referenceCurvePoints[index - 1]
    const nextReferencePoint = referenceCurvePoints[index]
    if (effectiveCompute > nextReferencePoint.effectiveCompute) continue
    const progressWithinSpan = (
      (effectiveCompute - previousReferencePoint.effectiveCompute) /
      (nextReferencePoint.effectiveCompute - previousReferencePoint.effectiveCompute)
    )
    const previousLogUplift = Math.log10(previousReferencePoint.uplift)
    const nextLogUplift = Math.log10(nextReferencePoint.uplift)
    return 10 ** (
      previousLogUplift +
      ((nextLogUplift - previousLogUplift) * progressWithinSpan)
    )
  }
  const trailingReferencePoints = referenceCurvePoints.slice(-2)
  const previousReferencePoint = trailingReferencePoints[0]
  const nextReferencePoint = trailingReferencePoints[1]
  const progressBeyondSpan = (
    (effectiveCompute - previousReferencePoint.effectiveCompute) /
    (nextReferencePoint.effectiveCompute - previousReferencePoint.effectiveCompute)
  )
  const previousLogUplift = Math.log10(previousReferencePoint.uplift)
  const nextLogUplift = Math.log10(nextReferencePoint.uplift)
  return 10 ** (
    previousLogUplift +
    ((nextLogUplift - previousLogUplift) * progressBeyondSpan)
  )
}

export const resolveScaleSettings = (
  overrides?: ExtrapolatedGraphScaleOverrides
): ExtrapolatedGraphScaleSettings => ({
  padding: {
    ...DEFAULT_SCALE_SETTINGS.padding,
    ...overrides?.padding,
  },
  yAxis: {
    ...DEFAULT_SCALE_SETTINGS.yAxis,
    ...overrides?.yAxis,
  },
  timeline: {
    ...DEFAULT_SCALE_SETTINGS.timeline,
    ...overrides?.timeline,
  },
  galaxy: {
    ...DEFAULT_SCALE_SETTINGS.galaxy,
    ...overrides?.galaxy,
  },
  animation: {
    ...DEFAULT_SCALE_SETTINGS.animation,
    ...overrides?.animation,
  },
})

const getLinearTickStep = (maxValue: number, tickCount: number) => {
  if (maxValue <= 0) return 1
  const roughStep = maxValue / tickCount
  const magnitude = 10 ** Math.floor(Math.log10(roughStep))
  const candidateSteps = [1, 2, 5, 10].map(step => step * magnitude)
  return candidateSteps.find(step => step >= roughStep) ?? candidateSteps[candidateSteps.length - 1]
}

const getRoundedLinearDomainMax = (maxValue: number, tickCount: number) => {
  const yTickStep = getLinearTickStep(maxValue, tickCount)
  return yTickStep * Math.max(1, Math.ceil(maxValue / yTickStep))
}

export const toGraphPoints = (data: GraphDatum[]): GraphPoint[] =>
  data.map(d => ({ ...d, timestamp: new Date(`${d.date}T00:00:00Z`).getTime() }))

export type PathPoint = {
  x: number
  y: number
}

const clampNumber = (value: number, min: number, max: number): number =>
  Math.max(min, Math.min(max, value))

const capPathPointToRenderBounds = (
  point: PathPoint,
  renderBounds?: GraphLineRenderBounds
): PathPoint => {
  if (!renderBounds) return point
  return {
    x: clampNumber(point.x, renderBounds.minX, renderBounds.maxX),
    y: clampNumber(point.y, renderBounds.minY, renderBounds.maxY),
  }
}

const capPathPointsToRenderBounds = (
  points: PathPoint[],
  renderBounds?: GraphLineRenderBounds
): PathPoint[] => {
  if (!renderBounds) return points
  return points.map(point => capPathPointToRenderBounds(point, renderBounds))
}

const graphPointsToPathPoints = (
  graphPoints: GraphPoint[],
  xForTimestamp: (timestamp: number) => number,
  yForValue: (value: number) => number
): PathPoint[] =>
  graphPoints.map(graphPoint => ({
    x: xForTimestamp(graphPoint.timestamp),
    y: yForValue(graphPoint.value),
  }))

const getBezierControlPoints = (
  points: PathPoint[],
  pointIndex: number,
  smoothing: number
): { cp1: PathPoint; cp2: PathPoint } => {
  const startPoint = points[pointIndex]
  const endPoint = points[pointIndex + 1]
  const previousPoint = points[pointIndex - 1] ?? startPoint
  const nextPoint = points[pointIndex + 2] ?? endPoint
  const handleScale = clampNumber(smoothing, 0, 1) / 6
  const minControlX = Math.min(startPoint.x, endPoint.x)
  const maxControlX = Math.max(startPoint.x, endPoint.x)
  const minControlY = Math.min(startPoint.y, endPoint.y)
  const maxControlY = Math.max(startPoint.y, endPoint.y)
  const cp1 = {
    x: startPoint.x + (endPoint.x - previousPoint.x) * handleScale,
    y: startPoint.y + (endPoint.y - previousPoint.y) * handleScale,
  }
  const cp2 = {
    x: endPoint.x - (nextPoint.x - startPoint.x) * handleScale,
    y: endPoint.y - (nextPoint.y - startPoint.y) * handleScale,
  }
  return {
    cp1: {
      ...cp1,
      x: clampNumber(cp1.x, minControlX, maxControlX),
      y: clampNumber(cp1.y, minControlY, maxControlY),
    },
    cp2: {
      ...cp2,
      x: clampNumber(cp2.x, minControlX, maxControlX),
      y: clampNumber(cp2.y, minControlY, maxControlY),
    },
  }
}

const getCubicBezierPoint = (
  startPoint: PathPoint,
  cp1: PathPoint,
  cp2: PathPoint,
  endPoint: PathPoint,
  progress: number
): PathPoint => {
  const inverse = 1 - progress
  const inverseSquared = inverse * inverse
  const progressSquared = progress * progress
  const startWeight = inverseSquared * inverse
  const cp1Weight = 3 * inverseSquared * progress
  const cp2Weight = 3 * inverse * progressSquared
  const endWeight = progressSquared * progress
  return {
    x: startPoint.x * startWeight
      + cp1.x * cp1Weight
      + cp2.x * cp2Weight
      + endPoint.x * endWeight,
    y: startPoint.y * startWeight
      + cp1.y * cp1Weight
      + cp2.y * cp2Weight
      + endPoint.y * endWeight,
  }
}

export const createSmoothedScreenPoints = (
  points: PathPoint[],
  smoothing: number,
  samplesPerSegment = 8
): PathPoint[] => {
  if (points.length < 2) return points
  if (smoothing <= 0 || samplesPerSegment <= 1) return points
  const clampedSmoothing = clampNumber(smoothing, 0, 1)
  const sampledPoints: PathPoint[] = [points[0]]
  for (let pointIndex = 0; pointIndex < points.length - 1; pointIndex += 1) {
    const startPoint = points[pointIndex]
    const endPoint = points[pointIndex + 1]
    const { cp1, cp2 } = getBezierControlPoints(
      points,
      pointIndex,
      clampedSmoothing
    )
    for (
      let sampleIndex = 1;
      sampleIndex <= samplesPerSegment;
      sampleIndex += 1
    ) {
      sampledPoints.push(getCubicBezierPoint(
        startPoint,
        cp1,
        cp2,
        endPoint,
        sampleIndex / samplesPerSegment
      ))
    }
  }
  return sampledPoints
}

// Overshoot-free variant of createSmoothedScreenPoints: monotone cubic
// (Fritsch–Carlson) tangents, so steep or log-scale climbs never dip past
// their data points the way the Bezier smoothing above can.
export const createMonotoneSmoothedScreenPoints = (
  points: PathPoint[],
  samplesPerSegment = 8
): PathPoint[] => {
  const n = points.length
  if (n < 2) return points.slice()
  const dx: number[] = [], m: number[] = [], t: number[] = new Array(n)
  for (let i = 0; i < n - 1; i++) {
    dx[i] = points[i + 1].x - points[i].x
    m[i] = (points[i + 1].y - points[i].y) / (dx[i] || 1e-6)
  }
  t[0] = m[0]
  t[n - 1] = m[n - 2]
  for (let i = 1; i < n - 1; i++) {
    if (m[i - 1] * m[i] <= 0) t[i] = 0
    else {
      const w1 = 2 * dx[i] + dx[i - 1], w2 = dx[i] + 2 * dx[i - 1]
      t[i] = (w1 + w2) / (w1 / m[i - 1] + w2 / m[i])
    }
  }
  const out: PathPoint[] = [{ x: points[0].x, y: points[0].y }]
  for (let i = 0; i < n - 1; i++) {
    const x0 = points[i].x, y0 = points[i].y, x1 = points[i + 1].x, y1 = points[i + 1].y, h = dx[i]
    const c1x = x0 + h / 3, c1y = y0 + t[i] * h / 3, c2x = x1 - h / 3, c2y = y1 - t[i + 1] * h / 3
    for (let s = 1; s <= samplesPerSegment; s++) {
      const u = s / samplesPerSegment, mu = 1 - u
      out.push({
        x: mu * mu * mu * x0 + 3 * mu * mu * u * c1x + 3 * mu * u * u * c2x + u * u * u * x1,
        y: mu * mu * mu * y0 + 3 * mu * mu * u * c1y + 3 * mu * u * u * c2y + u * u * u * y1,
      })
    }
  }
  return out
}

const buildPathFromScreenPoints = (
  points: PathPoint[],
  smoothing: number,
  flattenSmoothedCurves = false,
  renderBounds?: GraphLineRenderBounds
): string => {
  if (points.length === 0) return ''
  const renderPoints = capPathPointsToRenderBounds(points, renderBounds)
  if (flattenSmoothedCurves && smoothing > 0 && renderPoints.length >= 3) {
    return createSmoothedScreenPoints(renderPoints, smoothing)
      .map((point, pointIndex) =>
        `${pointIndex === 0 ? 'M' : 'L'} ${point.x} ${point.y}`
      )
      .join(' ')
  }
  if (smoothing <= 0 || renderPoints.length < 3) {
    return renderPoints.map((point, pointIndex) =>
      `${pointIndex === 0 ? 'M' : 'L'} ${point.x} ${point.y}`
    ).join(' ')
  }
  const clampedSmoothing = clampNumber(smoothing, 0, 1)
  const pathParts = [`M ${renderPoints[0].x} ${renderPoints[0].y}`]
  for (let pointIndex = 0; pointIndex < renderPoints.length - 1; pointIndex += 1) {
    const endPoint = renderPoints[pointIndex + 1]
    const { cp1, cp2 } = getBezierControlPoints(
      renderPoints,
      pointIndex,
      clampedSmoothing
    )
    pathParts.push(
      `C ${cp1.x} ${cp1.y} ${cp2.x} ${cp2.y} ${endPoint.x} ${endPoint.y}`
    )
  }
  return pathParts.join(' ')
}

const buildPath = (
  graphPoints: GraphPoint[],
  xForTimestamp: (timestamp: number) => number,
  yForValue: (value: number) => number,
  smoothing = 0,
  flattenSmoothedCurves = false,
  renderBounds?: GraphLineRenderBounds
) => {
  const points = graphPointsToPathPoints(graphPoints, xForTimestamp, yForValue)
  return buildPathFromScreenPoints(
    points,
    smoothing,
    flattenSmoothedCurves,
    renderBounds
  )
}

const getInterpolatedValue = (targetTimestamp: number, graphPoints: GraphPoint[]) => {
  if (targetTimestamp <= graphPoints[0].timestamp) return graphPoints[0].value
  if (targetTimestamp >= graphPoints[graphPoints.length - 1].timestamp) {
    return graphPoints[graphPoints.length - 1].value
  }
  for (let index = 1; index < graphPoints.length; index++) {
    const previousPoint = graphPoints[index - 1]
    const nextPoint = graphPoints[index]
    if (targetTimestamp > nextPoint.timestamp) continue
    const timestampSpan = nextPoint.timestamp - previousPoint.timestamp
    const progressWithinSpan = (targetTimestamp - previousPoint.timestamp) / timestampSpan
    return previousPoint.value + ((nextPoint.value - previousPoint.value) * progressWithinSpan)
  }
  return graphPoints[graphPoints.length - 1].value
}

const getYearFractionFromTimestamp = (ts: number) => {
  const d = new Date(ts)
  const year = d.getUTCFullYear()
  const yearStart = Date.UTC(year, 0, 1)
  const yearEnd = Date.UTC(year + 1, 0, 1)
  return year + (ts - yearStart) / (yearEnd - yearStart)
}

export const createLinearYearXForTimestamp = ({
  padding,
  innerWidth,
  leftYear,
  rightYear,
}: {
  padding: GraphPadding
  innerWidth: number
  leftYear: number
  rightYear: number
}) => {
  if (leftYear === rightYear) return () => padding.left + (innerWidth / 2)
  return (timestamp: number) => {
    const clampedYear = Math.min(
      rightYear,
      Math.max(leftYear, getYearFractionFromTimestamp(timestamp)),
    )
    return padding.left + (((clampedYear - leftYear) / (rightYear - leftYear)) * innerWidth)
  }
}

export const createCurrentPoint = (
  currentTimestamp: number,
  graphPoints: GraphPoint[]
): CurrentGraphPoint => ({
  date: new Date(currentTimestamp).toISOString(),
  timestamp: currentTimestamp,
  value: getInterpolatedValue(currentTimestamp, graphPoints),
})

export const getTimelineBounds = (
  graphPoints: GraphPoint[],
  currentTimestamp: number,
  showGalaxy: boolean,
  timelineSettings: TimelineScaleSettings
) => {
  const earliestTimestamp = Math.max(
    graphPoints[0].timestamp,
    timelineSettings.fixedStartTimestamp ?? graphPoints[0].timestamp,
  )
  const latestTimestamp = graphPoints[graphPoints.length - 1].timestamp
  const currentYear = new Date(currentTimestamp).getUTCFullYear()
  // futureWindowYears may be fractional (e.g. 1.5 → mid-year edge); Date.UTC
  // truncates a fractional year, so convert the fraction to months ourselves.
  const windowEndYears = currentYear + timelineSettings.futureWindowYears
  const windowEndWholeYear = Math.floor(windowEndYears)
  const windowEndTimestamp =
    timelineSettings.fixedEndTimestamp ??
    Date.UTC(
      windowEndWholeYear,
      Math.round((windowEndYears - windowEndWholeYear) * 12),
      1,
    )
  const cappedLatestTimestamp = Math.min(
    latestTimestamp,
    windowEndTimestamp,
  )
  const horizontalSpan = Math.max(
    currentTimestamp - earliestTimestamp,
    cappedLatestTimestamp - currentTimestamp,
  )
  return {
    domainEnd: currentTimestamp + horizontalSpan,
    effectiveCappedTimestamp: showGalaxy ? latestTimestamp : cappedLatestTimestamp,
  }
}

export const getVisibleGraphState = (
  graphPoints: GraphPoint[],
  currentPoint: CurrentGraphPoint,
  currentTimestamp: number,
  effectiveCappedTimestamp: number,
  // Optional left clamp: points earlier than this are hidden, so the visible
  // line / markers / axis begin here instead of at the first data point.
  startTimestamp?: number
): VisibleGraphState => {
  const afterStart = (graphPoint: GraphPoint) =>
    startTimestamp === undefined || graphPoint.timestamp >= startTimestamp
  const pastPointMarkers = graphPoints.filter(
    graphPoint => graphPoint.timestamp <= currentTimestamp
      && afterStart(graphPoint)
      && !graphPoint.isMorphFiller
  )
  const pastLinePoints = [...graphPoints.filter(graphPoint => graphPoint.timestamp < currentTimestamp && afterStart(graphPoint)), currentPoint]
  const futureLinePoints = [
    currentPoint,
    ...graphPoints.filter(graphPoint =>
      graphPoint.timestamp > currentTimestamp && graphPoint.timestamp <= effectiveCappedTimestamp
    ),
  ]
  // Morph fillers stay in the drawn line but never become dots/axis anchors.
  const futurePointMarkers = futureLinePoints
    .slice(1)
    .filter(graphPoint => !graphPoint.isMorphFiller)
  return {
    currentPoint,
    pastLinePoints,
    futureLinePoints,
    futurePointMarkers,
    pastPointMarkers,
    visibleGraphPoints: [...pastLinePoints, ...futurePointMarkers],
  }
}

export const createDefaultRenderSourceState = (
  visibleGraphState: VisibleGraphState
): GraphRenderSourceState => ({
  // The dashed capability line spans the FULL extent (start → capped end) and
  // is drawn UNDER the solid 'past' line, which overlays it up to the current
  // point. Anchoring the dashes to the line's fixed start — rather than to a
  // future path that begins at the moving current-point dot — keeps the dash
  // pattern still as the dot advances (the solid overlay simply grows to cover
  // more of it) instead of letting the dashes crawl along with the dot. Order
  // matters: the full dashed line first (bottom), the solid past line last
  // (top). pastLinePoints ends at the current point and futureLinePoints
  // begins at it, so slice(1) drops the duplicate seam point.
  lineSources: [
    {
      key: 'future',
      graphPoints: [
        ...visibleGraphState.pastLinePoints,
        ...visibleGraphState.futureLinePoints.slice(1),
      ],
      stroke: CAPABILITIES_LINE_COLOR,
      strokeWidth: CAPABILITIES_FUTURE_LINE_STROKE_WIDTH,
      strokeDasharray: CAPABILITIES_FUTURE_LINE_DASHARRAY,
    },
    {
      key: 'past',
      graphPoints: visibleGraphState.pastLinePoints,
      stroke: CAPABILITIES_LINE_COLOR,
      strokeWidth: GRAPH_LINE_STROKE_WIDTH,
    },
  ],
  futureMarkerSources: [
    {
      key: 'future',
      graphPoints: visibleGraphState.futurePointMarkers,
      stroke: CAPABILITIES_LINE_COLOR,
      r: 2.25,
    },
  ],
  pastMarkerSources: [
    {
      key: 'past',
      graphPoints: visibleGraphState.pastPointMarkers,
      fill: CAPABILITIES_LINE_COLOR,
      r: 3,
    },
  ],
  currentMarkerSources: [
    {
      key: 'current',
      graphPoints: [visibleGraphState.currentPoint],
      fill: CAPABILITIES_LINE_COLOR,
      r: 3,
    },
  ],
  visibleGraphPoints: visibleGraphState.visibleGraphPoints,
  currentValue: visibleGraphState.currentPoint.value,
})

export const appendProjectedLossOfControlRenderSourceState = ({
  renderSourceState,
  lossOfControlMinPoints,
  lossOfControlMaxPoints,
  effectiveCappedTimestamp,
  currentTimestamp,
}: {
  renderSourceState: GraphRenderSourceState
  lossOfControlMinPoints: GraphPoint[]
  lossOfControlMaxPoints: GraphPoint[]
  effectiveCappedTimestamp: number
  currentTimestamp: number
}): GraphRenderSourceState => {
  const visibleProjectedLossOfControlPoints = lossOfControlMinPoints.filter(graphPoint =>
    graphPoint.timestamp <= effectiveCappedTimestamp
  )
  const visibleProjectedLossOfControlTriplePoints = lossOfControlMaxPoints.filter(
    graphPoint => graphPoint.timestamp <= effectiveCappedTimestamp
  )
  const isDuring2028 = new Date(currentTimestamp).getUTCFullYear() === 2028
  const projectedLossOfControlStroke = PROJECTED_LOSS_OF_CONTROL_LOW_COLOR
  const projectedLossOfControlOpacity = isDuring2028
    ? PROJECTED_LOSS_OF_CONTROL_2028_OPACITY
    : 0.4
  const projectedLossOfControlSaturation = !isDuring2028
    ? undefined
    : PROJECTED_LOSS_OF_CONTROL_2028_SATURATION
  if (
    visibleProjectedLossOfControlPoints.length === 0 &&
    visibleProjectedLossOfControlTriplePoints.length === 0
  ) return renderSourceState
  return {
    ...renderSourceState,
    lineSources: [
      ...renderSourceState.lineSources,
      {
        key: 'projected-loss-of-control',
        graphPoints: visibleProjectedLossOfControlPoints,
        stroke: projectedLossOfControlStroke,
        strokeWidth: 1.5,
        strokeDasharray: '4,4',
        strokeOpacity: projectedLossOfControlOpacity,
        saturation: projectedLossOfControlSaturation,
      },
      {
        key: 'projected-loss-of-control-triple',
        graphPoints: visibleProjectedLossOfControlTriplePoints,
        stroke: PROJECTED_LOSS_OF_CONTROL_COLOR,
        strokeWidth: 1.5,
        strokeDasharray: '4,4',
        strokeOpacity: 1,
      },
    ],
    pastMarkerSources: [
      ...renderSourceState.pastMarkerSources,
      {
        key: 'projected-loss-of-control',
        graphPoints: visibleProjectedLossOfControlPoints,
        fill: projectedLossOfControlStroke,
        r: 1.5,
        fillOpacity: projectedLossOfControlOpacity,
        saturation: projectedLossOfControlSaturation,
      },
      {
        key: 'projected-loss-of-control-triple',
        graphPoints: visibleProjectedLossOfControlTriplePoints,
        fill: PROJECTED_LOSS_OF_CONTROL_COLOR,
        r: 1.5,
        fillOpacity: 1,
      },
    ],
    visibleGraphPoints: [
      ...renderSourceState.visibleGraphPoints,
      ...visibleProjectedLossOfControlPoints,
      ...visibleProjectedLossOfControlTriplePoints,
    ],
  }
}

export const getPrimaryVisibleGraphPoints = ({
  graphPoints,
  currentTimestamp,
  effectiveCappedTimestamp,
}: {
  graphPoints: GraphPoint[]
  currentTimestamp: number
  effectiveCappedTimestamp: number
}) => {
  const currentPoint = createCurrentPoint(currentTimestamp, graphPoints)
  return [
    ...graphPoints.filter(graphPoint => graphPoint.timestamp < currentTimestamp),
    currentPoint,
    ...graphPoints.filter(graphPoint =>
      graphPoint.timestamp > currentTimestamp && graphPoint.timestamp <= effectiveCappedTimestamp
    ),
  ]
}

export const createCapabilityThresholdOverlay = ({
  graphPoints,
  currentTimestamp,
  maxLabels,
  xForTimestamp,
  yForValue,
  width,
  height,
  padding,
  textColor,
  backgroundColor,
  activePlan,
}: {
  graphPoints: GraphPoint[]
  currentTimestamp: number
  maxLabels: number
  xForTimestamp: (timestamp: number) => number
  yForValue: (value: number) => number
  width: number
  height: number
  padding: GraphPadding
  textColor: string
  backgroundColor: string
  activePlan?: PlanId
}): CapabilityThresholdOverlay => {
  if (graphPoints.length < 2 || maxLabels <= 0) return { annotations: [], markers: [] }
  const thresholdLabelCharWidth = 6
  const thresholdLabelHeight = 10
  const thresholdLabelMaxOffset = 10
  // Milestones on the plan's own plotted scale (Plan A: shaped values with
  // the AC->TED->ASI ladder; branch plans: model-uplift cutoffs).
  const markerSpecs = getCapabilityMarkerSpecs(activePlan)
  const candidateThresholdMarkers = markerSpecs.flatMap(threshold => {
    const firstReachedThresholdGraphPointIndex = graphPoints.findIndex(
      graphPoint => graphPoint.value >= threshold.value
    )
    if (firstReachedThresholdGraphPointIndex === -1) return []
    const firstReachedThresholdGraphPoint = graphPoints[firstReachedThresholdGraphPointIndex]
    const previousThresholdGraphPoint = firstReachedThresholdGraphPointIndex > 0
      ? graphPoints[firstReachedThresholdGraphPointIndex - 1]
      : undefined
    const nextThresholdGraphPoint = firstReachedThresholdGraphPointIndex < graphPoints.length - 1
      ? graphPoints[firstReachedThresholdGraphPointIndex + 1]
      : undefined
    const thresholdMarkerX = xForTimestamp(firstReachedThresholdGraphPoint.timestamp)
    const thresholdMarkerY = yForValue(firstReachedThresholdGraphPoint.value)
    const previousThresholdGraphPointX = previousThresholdGraphPoint
      ? xForTimestamp(previousThresholdGraphPoint.timestamp)
      : undefined
    const previousThresholdGraphPointY = previousThresholdGraphPoint
      ? yForValue(previousThresholdGraphPoint.value)
      : undefined
    const nextThresholdGraphPointX = nextThresholdGraphPoint
      ? xForTimestamp(nextThresholdGraphPoint.timestamp)
      : undefined
    const nextThresholdGraphPointY = nextThresholdGraphPoint
      ? yForValue(nextThresholdGraphPoint.value)
      : undefined
    const thresholdTangentDeltaX = previousThresholdGraphPointX !== undefined
      && nextThresholdGraphPointX !== undefined
      ? nextThresholdGraphPointX - previousThresholdGraphPointX
      : previousThresholdGraphPointX !== undefined
        ? thresholdMarkerX - previousThresholdGraphPointX
        : nextThresholdGraphPointX !== undefined
          ? nextThresholdGraphPointX - thresholdMarkerX
          : 0
    const thresholdTangentDeltaY = previousThresholdGraphPointY !== undefined
      && nextThresholdGraphPointY !== undefined
      ? nextThresholdGraphPointY - previousThresholdGraphPointY
      : previousThresholdGraphPointY !== undefined
        ? thresholdMarkerY - previousThresholdGraphPointY
        : nextThresholdGraphPointY !== undefined
          ? nextThresholdGraphPointY - thresholdMarkerY
          : 0
    const thresholdTangentMagnitude = Math.abs(thresholdTangentDeltaX) + Math.abs(thresholdTangentDeltaY)
    const thresholdXOffsetMagnitude = thresholdTangentMagnitude > 0
      ? thresholdLabelMaxOffset * (Math.abs(thresholdTangentDeltaY) / thresholdTangentMagnitude)
      : 0
    const thresholdYOffsetMagnitude = thresholdTangentMagnitude > 0
      ? thresholdLabelMaxOffset * (Math.abs(thresholdTangentDeltaX) / thresholdTangentMagnitude)
      : thresholdLabelMaxOffset
    const milestoneEntry = capabilityMilestones.find(t => t.label === threshold.label)
    return [{
      key: `capability-threshold-${threshold.label}`,
      label: threshold.label,
      displayLabel: threshold.label,
      displayValue: threshold.displayValue,
      subtitle: milestoneEntry?.subtitle,
      color: milestoneEntry?.color,
      thresholdTooltip: milestoneEntry?.tooltip,
      timestamp: firstReachedThresholdGraphPoint.timestamp,
      x: thresholdMarkerX,
      y: thresholdMarkerY,
      labelX: thresholdMarkerX - thresholdXOffsetMagnitude,
      labelY: thresholdMarkerY - thresholdYOffsetMagnitude,
    }]
  })
  const sortedThresholdMarkers = [...candidateThresholdMarkers].sort(
    (thresholdMarkerA, thresholdMarkerB) => thresholdMarkerB.x - thresholdMarkerA.x
  )
  const keptThresholdMarkers: typeof candidateThresholdMarkers = []
  const thresholdRightBound = width - padding.right
  const thresholdBottomBound = height - padding.bottom
  for (const thresholdMarker of sortedThresholdMarkers) {
    const thresholdMarkerBox = {
      left: thresholdMarker.labelX - (thresholdMarker.displayLabel.length * thresholdLabelCharWidth),
      right: thresholdMarker.labelX,
      top: thresholdMarker.labelY - thresholdLabelHeight,
      bottom: thresholdMarker.labelY,
    }
    if (
      thresholdMarkerBox.left < padding.left ||
      thresholdMarkerBox.right > thresholdRightBound ||
      thresholdMarkerBox.top < padding.top ||
      thresholdMarkerBox.bottom > thresholdBottomBound
    ) continue
    let overlapsExistingMarker = false
    for (const keptThresholdMarker of keptThresholdMarkers) {
      const keptThresholdMarkerBox = {
        left: keptThresholdMarker.labelX - (keptThresholdMarker.displayLabel.length * thresholdLabelCharWidth),
        right: keptThresholdMarker.labelX,
        top: keptThresholdMarker.labelY - thresholdLabelHeight,
        bottom: keptThresholdMarker.labelY,
      }
      if (
        thresholdMarkerBox.right < keptThresholdMarkerBox.left ||
        thresholdMarkerBox.left > keptThresholdMarkerBox.right ||
        thresholdMarkerBox.bottom < keptThresholdMarkerBox.top ||
        thresholdMarkerBox.top > keptThresholdMarkerBox.bottom
      ) continue
      overlapsExistingMarker = true
      break
    }
    if (overlapsExistingMarker) continue
    keptThresholdMarkers.push(thresholdMarker)
    if (keptThresholdMarkers.length >= maxLabels) break
  }
  const visibleThresholdMarkers = [...keptThresholdMarkers].sort(
    (thresholdMarkerA, thresholdMarkerB) => thresholdMarkerA.x - thresholdMarkerB.x
  )
  const keptThresholdMarkerKeys = new Set(keptThresholdMarkers.map(thresholdMarker => thresholdMarker.key))
  return {
    annotations: candidateThresholdMarkers.map(thresholdMarker => {
      const isVisibleThresholdMarker = keptThresholdMarkerKeys.has(thresholdMarker.key)
      const visibleThresholdMarkerOpacity = thresholdMarker.timestamp > currentTimestamp ? 0.75 : 1
      const isPlanA = activePlan === 'A'
      const thresholdTooltipParts: React.ReactNode[] = [
        `${thresholdMarker.displayLabel} (${thresholdMarker.displayValue} speedup to AI research)`,
        ...(!isPlanA && thresholdMarker.subtitle ? [thresholdMarker.subtitle] : []),
        ...(thresholdMarker.thresholdTooltip ? [thresholdMarker.thresholdTooltip] : []),
      ]
      return {
      key: thresholdMarker.key,
      x: thresholdMarker.labelX,
      y: thresholdMarker.labelY,
      text: thresholdMarker.displayLabel,
      subtitle: isPlanA ? undefined : thresholdMarker.subtitle,
      tooltip: thresholdTooltipParts,
      color: isPlanA ? textColor : (thresholdMarker.color ?? textColor),
      textShadowColor: backgroundColor,
      opacity: isVisibleThresholdMarker ? visibleThresholdMarkerOpacity : 0,
      transitionDurationMs: GRAPH_LABEL_FADE_DURATION_MS,
      transform: 'translate(-100%, -100%)',
      pointerEvents: isVisibleThresholdMarker ? 'auto' as const : 'none' as const,
    }}),
    markers: visibleThresholdMarkers.map(thresholdMarker => {
      const isPlanA = activePlan === 'A'
      const markerColor = isPlanA ? textColor : (thresholdMarker.color ?? textColor)
      return {
        key: `${thresholdMarker.key}-marker`,
        cx: thresholdMarker.x,
        cy: thresholdMarker.y,
        r: 1.6,
        fill: backgroundColor,
        stroke: markerColor,
        strokeWidth: 0.8,
        innerR: thresholdMarker.timestamp > currentTimestamp ? 0.75 : undefined,
        innerFill: thresholdMarker.timestamp > currentTimestamp ? markerColor : undefined,
        innerFillOpacity: thresholdMarker.timestamp > currentTimestamp ? 1 : undefined,
      }
    }),
  }
}

export const getYDomainMax = (
  currentValue: number,
  maxVisibleValue: number,
  maxFutureValue: number,
  yAxisSettings: YScaleSettings
) => {
  const resolvedFixedMax = yAxisSettings.fixedMax === 'maxVisibleValue'
    ? maxVisibleValue
    : yAxisSettings.fixedMax === 'maxFutureValue'
      ? maxFutureValue
      : yAxisSettings.fixedMax
  if (yAxisSettings.mode === 'fixed') return resolvedFixedMax
  if (yAxisSettings.mode === 'auto') {
    return getRoundedLinearDomainMax(maxVisibleValue, yAxisSettings.tickCount)
  }
  if (currentValue <= yAxisSettings.switchThreshold) {
    return resolvedFixedMax
  }
  return getRoundedLinearDomainMax(maxVisibleValue, yAxisSettings.tickCount)
}

export const getVisibleDataPointTimestamps = (renderSourceState: GraphRenderSourceState) =>
  Array.from(new Set([
    ...renderSourceState.pastMarkerSources.flatMap(markerSource =>
      markerSource.graphPoints.map(graphPoint => graphPoint.timestamp)
    ),
    ...renderSourceState.futureMarkerSources.flatMap(markerSource =>
      markerSource.graphPoints.map(graphPoint => graphPoint.timestamp)
    ),
  ])).sort((timestampA, timestampB) => timestampA - timestampB)

export const createEvenlySpacedTimestampPositions = ({
  visibleTimestamps,
  padding,
  innerWidth,
}: {
  visibleTimestamps: number[]
  padding: GraphPadding
  innerWidth: number
}): TimestampPosition[] => {
  if (visibleTimestamps.length === 0) return []
  if (visibleTimestamps.length === 1) {
    return [{
      timestamp: visibleTimestamps[0],
      x: padding.left + (innerWidth / 2),
    }]
  }
  const lastTimestampIndex = visibleTimestamps.length - 1
  return visibleTimestamps.map((timestamp, index) => ({
    timestamp,
    x: padding.left + ((index / lastTimestampIndex) * innerWidth),
  }))
}

export const getInterpolatedXFromTimestampPositions = ({
  timestamp,
  timestampPositions,
  padding,
  innerWidth,
}: {
  timestamp: number
  timestampPositions: TimestampPosition[]
  padding: GraphPadding
  innerWidth: number
}) => {
  if (timestampPositions.length === 0) return padding.left + (innerWidth / 2)
  if (timestampPositions.length === 1) return timestampPositions[0].x
  if (timestamp <= timestampPositions[0].timestamp) return timestampPositions[0].x
  const lastTimestampPosition = timestampPositions[timestampPositions.length - 1]
  if (timestamp >= lastTimestampPosition.timestamp) return lastTimestampPosition.x
  for (let index = 1; index < timestampPositions.length; index++) {
    const previousTimestampPosition = timestampPositions[index - 1]
    const nextTimestampPosition = timestampPositions[index]
    if (timestamp > nextTimestampPosition.timestamp) continue
    const timestampSpan = nextTimestampPosition.timestamp - previousTimestampPosition.timestamp
    const progressWithinSpan = (timestamp - previousTimestampPosition.timestamp) / timestampSpan
    return previousTimestampPosition.x + (
      (nextTimestampPosition.x - previousTimestampPosition.x) * progressWithinSpan
    )
  }
  return lastTimestampPosition.x
}

export const createXForTimestamp = ({
  showGalaxy,
  padding,
  innerWidth,
  galaxySettings,
  timestampPositions,
}: {
  showGalaxy: boolean
  padding: GraphPadding
  innerWidth: number
  galaxySettings: GalaxyScaleSettings
  timestampPositions: TimestampPosition[]
}) => {
  if (showGalaxy) {
    const galaxyYearSpan = Math.max(1, galaxySettings.maxYear - galaxySettings.referenceYear)
    const galaxyDomainMax = Math.max(1, Math.log10(galaxyYearSpan))
    return (timestamp: number) => {
      const yearsSinceReference = Math.max(
        1,
        getYearFractionFromTimestamp(timestamp) - galaxySettings.referenceYear,
      )
      const logVal = Math.log10(yearsSinceReference)
      return padding.left + (logVal / galaxyDomainMax) * innerWidth
    }
  }
  return (timestamp: number) => getInterpolatedXFromTimestampPositions({
    timestamp,
    timestampPositions,
    padding,
    innerWidth,
  })
}

const LOG_SCALE_MIN_VALUE = 1

export const createYForValue = ({
  padding,
  innerHeight,
  yDomainMax,
  yDomainMin = 0,
  useLogScale = false,
}: {
  padding: GraphPadding
  innerHeight: number
  yDomainMax: number
  // Value mapped to the bottom of the plot (linear scale only). Defaults to 0;
  // set >0 to lift the floor so a small-range curve starts at the baseline
  // instead of floating up from zero.
  yDomainMin?: number
  useLogScale?: boolean
}) => {
  if (useLogScale) {
    const logDomainMax = Math.log10(Math.max(yDomainMax, LOG_SCALE_MIN_VALUE * 10))
    return (value: number) => {
      const clampedValue = Math.max(value, LOG_SCALE_MIN_VALUE)
      const scaledValue = Math.log10(clampedValue) / logDomainMax
      return padding.top + ((1 - scaledValue) * innerHeight)
    }
  }
  const domainSpan = yDomainMax - yDomainMin || yDomainMax || 1
  return (value: number) => {
    const scaledValue = (value - yDomainMin) / domainSpan
    return padding.top + ((1 - scaledValue) * innerHeight)
  }
}

// Closed-form inverse of `createYForValue` (linear and log branches),
// reusing the same domain/padding so it matches whatever scale the graph
// is rendering with. Returns the compute value at a given screen Y.
export const createValueForY = ({
  padding,
  innerHeight,
  yDomainMax,
  yDomainMin = 0,
  useLogScale = false,
}: {
  padding: GraphPadding
  innerHeight: number
  yDomainMax: number
  yDomainMin?: number
  useLogScale?: boolean
}) => {
  if (innerHeight <= 0) return () => 0
  if (useLogScale) {
    const logDomainMax = Math.log10(Math.max(yDomainMax, LOG_SCALE_MIN_VALUE * 10))
    return (screenY: number): number => {
      const scaledValue = 1 - (screenY - padding.top) / innerHeight
      return Math.pow(10, scaledValue * logDomainMax)
    }
  }
  const domainSpan = yDomainMax - yDomainMin || yDomainMax || 1
  return (screenY: number): number => {
    const scaledValue = 1 - (screenY - padding.top) / innerHeight
    return yDomainMin + scaledValue * domainSpan
  }
}

export const createRenderedLineSegments = (
  lineSources: GraphLineSource[],
  xForTimestamp: (timestamp: number) => number,
  yForValue: (value: number) => number,
  smoothing = 0,
  flattenSmoothedCurves = false,
  renderBounds?: GraphLineRenderBounds
): ExtrapolatedGraphLineSegment[] =>
  lineSources.flatMap(lineSource => {
    if (lineSource.graphPoints.length < 2) return []
    return [{
      key: lineSource.key,
      d: buildPath(
        lineSource.graphPoints,
        xForTimestamp,
        yForValue,
        smoothing,
        flattenSmoothedCurves,
        renderBounds
      ),
      stroke: lineSource.stroke,
      strokeWidth: lineSource.strokeWidth,
      strokeDasharray: lineSource.strokeDasharray,
      strokeOpacity: lineSource.strokeOpacity,
      saturation: lineSource.saturation,
    }]
  })

export const createRenderedFutureMarkerDashSegments = (
  lineSources: GraphLineSource[],
  futureMarkerSources: GraphMarkerSource[],
  xForTimestamp: (timestamp: number) => number,
  yForValue: (value: number) => number
): ExtrapolatedGraphLineSegment[] =>
  futureMarkerSources.flatMap(markerSource => {
    const matchingLineSource = lineSources.find(lineSource => lineSource.key === markerSource.key)
    if (!matchingLineSource) return []
    return markerSource.graphPoints.flatMap(markerGraphPoint => {
      const markerPointIndex = matchingLineSource.graphPoints.findIndex(
        graphPoint => graphPoint.date === markerGraphPoint.date
      )
      if (markerPointIndex === -1) return []
      const previousGraphPoint = matchingLineSource.graphPoints[markerPointIndex - 1]
      const nextGraphPoint = matchingLineSource.graphPoints[markerPointIndex + 1]
      const anchorGraphPoint = previousGraphPoint ?? nextGraphPoint
      if (!anchorGraphPoint) return []
      const markerX = xForTimestamp(markerGraphPoint.timestamp)
      const markerY = yForValue(markerGraphPoint.value)
      const anchorX = xForTimestamp(anchorGraphPoint.timestamp)
      const anchorY = yForValue(anchorGraphPoint.value)
      const deltaX = markerX - anchorX
      const deltaY = markerY - anchorY
      const segmentLength = Math.hypot(deltaX, deltaY)
      if (segmentLength === 0) return []
      const dashLength = 4
      const dashRadius = dashLength / 2
      const unitX = deltaX / segmentLength
      const unitY = deltaY / segmentLength
      return [{
        key: `${markerSource.key}-${markerGraphPoint.date}-dash`,
        d: `M ${markerX - (unitX * dashRadius)} ${markerY - (unitY * dashRadius)} L ${markerX + (unitX * dashRadius)} ${markerY + (unitY * dashRadius)}`,
        stroke: matchingLineSource.stroke,
        strokeWidth: matchingLineSource.strokeWidth,
        strokeOpacity: matchingLineSource.strokeOpacity,
        saturation: matchingLineSource.saturation,
      }]
    })
  })

export const createRenderedMarkers = (
  markerSources: GraphMarkerSource[],
  xForTimestamp: (timestamp: number) => number,
  yForValue: (value: number) => number
): ExtrapolatedGraphPointMarker[] =>
  markerSources.flatMap(markerSource =>
    markerSource.graphPoints.map(graphPoint => ({
      // Single-point sources (the current "now" dot) key on the source alone:
      // their point's date changes EVERY FRAME while the date animates, and a
      // date-based key made React remount the marker element per frame.
      key: markerSource.graphPoints.length === 1
        ? markerSource.key
        : `${markerSource.key}-${graphPoint.date}`,
      date: graphPoint.date,
      timestamp: graphPoint.timestamp,
      value: graphPoint.value,
      cx: xForTimestamp(graphPoint.timestamp),
      cy: yForValue(graphPoint.value),
      r: markerSource.r,
      fill: markerSource.fill,
      stroke: markerSource.stroke,
      strokeWidth: markerSource.strokeWidth,
      fillOpacity: markerSource.fillOpacity,
      saturation: markerSource.saturation,
    }))
  )
